import { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
// form
import { FormProvider as Form } from "react-hook-form";
import { SaveBar } from "@shopify/app-bridge-react";

let formIdCounter = 0;

FormProvider.propTypes = {
  children: PropTypes.node.isRequired,
  methods: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  showSaveBar: PropTypes.bool,
  hasUnsavedChanges: PropTypes.bool,
};

export default function FormProvider({ children, onSubmit, methods, showSaveBar = undefined, hasUnsavedChanges }) {
  const formIdRef = useRef(null);
  if (formIdRef.current === null) formIdRef.current = `form-save-bar-${++formIdCounter}`;
  const formId = formIdRef.current;

  // Reading formState during render subscribes to these fields (RHF proxy).
  const { isDirty: formIsDirty, isSubmitting, isSubmitSuccessful, submitCount } = methods.formState;

  // After a failed save attempt (e.g. validation errors), hide the save bar so
  // it doesn't keep popping up. The merchant must make another change to bring
  // it back, at which point they can retry saving.
  const [dismissedAfterError, setDismissedAfterError] = useState(false);

  useEffect(() => {
    if (submitCount > 0 && !isSubmitting && !isSubmitSuccessful) {
      setDismissedAfterError(true);
    }
  }, [submitCount, isSubmitting, isSubmitSuccessful]);

  useEffect(() => {
    const subscription = methods.watch((_, info) => {
      if (info?.type === "change") setDismissedAfterError(false);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  // Drive the contextual save bar from react-hook-form's dirty state (same as
  // the V2 flow). Unlike App Bridge's automatic `data-save-bar` detection, RHF
  // tracks every registered field, so dropdowns and color pickers open the bar.
  //
  // Also keep it closed while submitting/after success (prevents the bar staying
  // "hung" when a page navigates away during submit, e.g. adding products) and
  // after a failed save until the next change.
  const open =
    (hasUnsavedChanges ?? formIsDirty) && !isSubmitting && !isSubmitSuccessful && !dismissedAfterError;

  // V1 pages historically relied on App Bridge auto-hiding the bar on submit.
  // Now that visibility is driven by dirty state, clear it after a successful
  // submit so the bar stays closed without each page having to reset itself.
  useEffect(() => {
    if (showSaveBar && isSubmitSuccessful) {
      methods.reset(methods.getValues());
    }
  }, [showSaveBar, isSubmitSuccessful, methods]);

  const handleReset = () => {
    methods.reset();
  };

  return (
    <Form {...methods}>
      {showSaveBar && (
        <SaveBar open={open}>
          <button variant="primary" type="submit" form={formId} />
          <button type="reset" form={formId} />
        </SaveBar>
      )}
      <form id={formId} onSubmit={onSubmit} onReset={handleReset} noValidate>
        {children}
      </form>
    </Form>
  );
}
